import React from "react";
import { Route, Routes } from "react-router-dom";
import { Layout } from "./pages/layout/Layout";
import { useIsMobile } from "./hooks/useIsMobile";
import { AppBootScreen, ContentRegionLoader } from "./components/loading";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useTenantConfig } from "./admin/components/TenantThemeProvider";
import {
  ChatHistoryPanel,
  KNOWLEDGE_PAGE_SIZE,
} from "./components/ChatHistoryPanel/ChatHistoryPanel";
import { KnowledgeThread } from "./blades/KnowledgeThread";
import { AgentsBlade } from "./blades/AgentsBlade";
import { OverviewBlade } from "./blades/OverviewBlade";
import { DecisionsBlade } from "./blades/DecisionsBlade";
import { ReviewBlade } from "./blades/ReviewBlade";
import { InstancesBlade } from "./blades/InstancesBlade";
import { SystemActionsBlade } from "./blades/SystemActionsBlade";
import { GitCheckoutsBlade } from "./blades/GitCheckoutsBlade";
import { useConsoleAuth } from "./auth/AuthProvider";
import { CONSOLE_RENEWABLE_REFUSAL_REASON } from "./auth/sessionConstants";
import Login from "./pages/Login";
import {
  askConsoleKnowledgeStream,
  deleteConsoleTrail,
  fetchConsoleAgents,
  fetchConsoleDecisions,
  fetchConsoleGitCheckouts,
  fetchConsoleHistoryPage,
  fetchConsoleInstances,
  fetchConsoleOverview,
  fetchConsoleReview,
  fetchConsoleSession,
  fetchConsoleSystemActions,
  fetchConsoleTrail,
  renameConsoleTrail,
  ruleConsoleDecision,
} from "./api/consoleApi";
import {
  askConversationId,
  historyConversation,
  isAskAnswer,
} from "./api/conversationIdentity";
import type {
  ConsoleAgentsBoardResponse,
  ConsoleAgentsScope,
  ConsoleAskResponse,
  ConsoleDecisionsResponse,
  ConsoleGitCheckoutsResponse,
  ConsoleHistoryResponse,
  ConsoleInstancesResponse,
  ConsoleOverviewResponse,
  ConsoleRefusal,
  ConsoleReviewResponse,
  ConsoleRulingChoice,
  ConsoleSessionResponse,
  ConsoleSystemActionsResponse,
  ConsoleTrailDetail,
  ConsoleTrailSummary,
} from "./api/types";

/**
 * The blade the console opens on, the two the agent registry package adds,
 * Decisions and Review, Instances, and the System Action Log.
 */
export const BLADE_WORK = "work";
export const BLADE_AGENTS = "agents";
export const BLADE_OVERVIEW = "overview";
export const BLADE_DECISIONS = "decisions";
export const BLADE_REVIEW = "review";
export const BLADE_INSTANCES = "instances";
export const BLADE_SYSTEM_ACTIONS = "system_actions";
export const BLADE_GIT_CHECKOUTS = "git_checkouts";

export const NO_ROLE_MESSAGE =
  "Your account is signed in but has no Cortex Console role yet. Ask your manager to " +
  "add you to a Cortex Console group, then sign in again.";

function isSession(
  body: ConsoleSessionResponse | ConsoleRefusal,
): body is ConsoleSessionResponse {
  return Array.isArray((body as ConsoleSessionResponse).allowed_blades);
}

function isHistoryAnswer(
  body: ConsoleHistoryResponse | ConsoleRefusal,
): body is ConsoleHistoryResponse {
  const candidate = body as ConsoleHistoryResponse;
  return Array.isArray(candidate.conversations) && candidate.owner_match !== undefined;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <ConsoleShell />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function ConsoleShell() {
  const { ready, error: brandingError } = useTenantConfig();
  const { account, signOut, getIdToken, releaseStaleSession } = useConsoleAuth();
  const [session, setSession] = React.useState<ConsoleSessionResponse | null>(null);
  const [refusal, setRefusal] = React.useState<ConsoleRefusal | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [conversations, setConversations] = React.useState<ConsoleTrailSummary[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyError, setHistoryError] = React.useState<string | null>(null);
  const [hasMore, setHasMore] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [openTrail, setOpenTrail] = React.useState<ConsoleTrailDetail | null>(null);
  const [answer, setAnswer] = React.useState<ConsoleAskResponse | ConsoleRefusal | null>(
    null,
  );
  const [asking, setAsking] = React.useState(false);
  const [activeBladeId, setActiveBladeId] = React.useState<string>(BLADE_OVERVIEW);
  const initialBladeResolvedRef = React.useRef(false);
  const [board, setBoard] = React.useState<
    ConsoleAgentsBoardResponse | ConsoleRefusal | null
  >(null);
  const [boardLoading, setBoardLoading] = React.useState(false);
  const [boardScope, setBoardScope] =
    React.useState<ConsoleAgentsScope>("granted_streams");
  const [overview, setOverview] = React.useState<
    ConsoleOverviewResponse | ConsoleRefusal | null
  >(null);
  const [overviewLoading, setOverviewLoading] = React.useState(false);
  const [decisions, setDecisions] = React.useState<
    ConsoleDecisionsResponse | ConsoleRefusal | null
  >(null);
  const [decisionsLoading, setDecisionsLoading] = React.useState(false);
  const [decisionsScope, setDecisionsScope] =
    React.useState<ConsoleAgentsScope>("granted_streams");
  const [followedDecisionKey, setFollowedDecisionKey] = React.useState<string | null>(
    null,
  );
  const [review, setReview] = React.useState<
    ConsoleReviewResponse | ConsoleRefusal | null
  >(null);
  const [reviewLoading, setReviewLoading] = React.useState(false);
  const [reviewScope, setReviewScope] =
    React.useState<ConsoleAgentsScope>("granted_streams");
  const [instances, setInstances] = React.useState<
    ConsoleInstancesResponse | ConsoleRefusal | null
  >(null);
  const [instancesLoading, setInstancesLoading] = React.useState(false);
  const [systemActions, setSystemActions] = React.useState<
    ConsoleSystemActionsResponse | ConsoleRefusal | null
  >(null);
  const [systemActionsLoading, setSystemActionsLoading] = React.useState(false);
  const [gitCheckouts, setGitCheckouts] = React.useState<
    ConsoleGitCheckoutsResponse | ConsoleRefusal | null
  >(null);
  const [gitCheckoutsLoading, setGitCheckoutsLoading] = React.useState(false);
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(isMobile);
  React.useEffect(() => {
    setSidebarCollapsed(isMobile);
  }, [isMobile]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchOpen, setSearchOpen] = React.useState(false);

  const loadHistory = React.useCallback(
    async (offset = 0) => {
      setHistoryLoading(true);
      const result = await fetchConsoleHistoryPage(
        getIdToken,
        KNOWLEDGE_PAGE_SIZE,
        offset,
      );
      if (isHistoryAnswer(result.body)) {
        const rows = result.body.conversations || [];
        setConversations((current) => (offset === 0 ? rows : [...current, ...rows]));
        setHasMore(rows.length === KNOWLEDGE_PAGE_SIZE);
        setHistoryError(null);
      } else if (offset === 0) {
        setConversations([]);
        setHasMore(false);
        setHistoryError(
          result.body.message || "Cortex could not load your conversations. Try again in a moment.",
        );
      }
      setHistoryLoading(false);
    },
    [getIdToken],
  );

  /**
   * The Agents board (harness row CC1B-12). The answer is rendered whatever its
   * outcome: a clarification about scope, a safe failure or a refusal is the API's
   * to state, and the SPA shows what it said rather than deciding anything itself.
   */
  const loadBoard = React.useCallback(
    async (scope: ConsoleAgentsScope) => {
      setBoardLoading(true);
      const result = await fetchConsoleAgents(getIdToken, scope);
      setBoard(result.body);
      setBoardLoading(false);
    },
    [getIdToken],
  );

  /**
   * The Overview (harness row CC1B-13). Rendered whatever its outcome, for the same
   * reason the board is: the rundown, the counts and the fleet are the API's to state,
   * and a read never asks the Agentic Operator for a rundown it has not written.
   */
  const loadOverview = React.useCallback(async () => {
    setOverviewLoading(true);
    const result = await fetchConsoleOverview(getIdToken);
    setOverview(result.body);
    setOverviewLoading(false);
  }, [getIdToken]);

  /**
   * The Decisions blade (harness row CC2-10). Rendered whatever its outcome, for the
   * same reason the board is: the lists, the scope and who may rule are the API's to
   * state.
   */
  const loadDecisions = React.useCallback(
    async (scope: ConsoleAgentsScope) => {
      setDecisionsLoading(true);
      const result = await fetchConsoleDecisions(getIdToken, scope);
      setDecisions(result.body);
      setDecisionsLoading(false);
    },
    [getIdToken],
  );

  /**
   * The Review blade (harness row CC2-11). Rendered whatever its outcome, for the same
   * reason the board is: the columns, the cards and the scope are the API's to state.
   */
  const loadReview = React.useCallback(
    async (scope: ConsoleAgentsScope) => {
      setReviewLoading(true);
      const result = await fetchConsoleReview(getIdToken, scope);
      setReview(result.body);
      setReviewLoading(false);
    },
    [getIdToken],
  );

  /**
   * The Instances blade (harness row CC3-09/CC3-11). Rendered whatever its outcome,
   * for the same reason the board and the Overview are: eligibility and drift state
   * are the API's to state, and a read never computes drift or reaches a worktree.
   */
  const loadInstances = React.useCallback(async () => {
    setInstancesLoading(true);
    const result = await fetchConsoleInstances(getIdToken);
    setInstances(result.body);
    setInstancesLoading(false);
  }, [getIdToken]);

  /**
   * The System Action Log (harness row CDAM-06). Rendered whatever its outcome, for
   * the same reason the board and Instances are: eligibility and shape are the API's
   * to state, and this read never touches the Decisions blade's own repository read.
   */
  const loadSystemActions = React.useCallback(async () => {
    setSystemActionsLoading(true);
    const result = await fetchConsoleSystemActions(getIdToken);
    setSystemActions(result.body);
    setSystemActionsLoading(false);
  }, [getIdToken]);

  /**
   * The Git Checkouts blade (harness row CC3B-05). Rendered whatever its outcome,
   * for the same reason Instances is: grouping by operator is the API's to state,
   * and a read never buckets a checkout of its own.
   */
  const loadGitCheckouts = React.useCallback(async () => {
    setGitCheckoutsLoading(true);
    const result = await fetchConsoleGitCheckouts(getIdToken);
    setGitCheckouts(result.body);
    setGitCheckoutsLoading(false);
  }, [getIdToken]);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const result = await fetchConsoleSession(getIdToken);
      if (cancelled) {
        return;
      }
      if (isSession(result.body)) {
        setSession(result.body);
        setRefusal(null);
      } else {
        setSession(null);
        setRefusal(result.body);
        if (result.body.outcome_type === "blocked") {
          if (result.body.reason === CONSOLE_RENEWABLE_REFUSAL_REASON) {
            // Renewable: the request path has already forced one refresh and retried
            // (consoleApi.request). Still expired means silent renewal is gone, so ask
            // once more on this user-initiated path - that is the one place interactive
            // recovery may start. The account is not dropped for an expired token.
            void getIdToken({ forceRefresh: true });
          } else {
            releaseStaleSession();
          }
        }
      }
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [getIdToken, releaseStaleSession]);

  React.useEffect(() => {
    if (session && session.roles_projected.length > 0) {
      void loadHistory(0);
    }
  }, [session, loadHistory]);

  React.useEffect(() => {
    if (!session || session.roles_projected.length === 0 || initialBladeResolvedRef.current) {
      return;
    }
    initialBladeResolvedRef.current = true;
    const overviewBlade = session.allowed_blades.find((blade) => blade.id === BLADE_OVERVIEW);
    if (overviewBlade?.enabled) {
      setActiveBladeId(BLADE_OVERVIEW);
      void loadOverview();
      return;
    }
    setActiveBladeId(BLADE_WORK);
  }, [session, loadOverview]);

  const startNew = () => {
    setActiveBladeId(BLADE_WORK);
    setSelectedId(null);
    setOpenTrail(null);
    setAnswer(null);
  };

  const selectBlade = (bladeId: string) => {
    setActiveBladeId(bladeId);
    setFollowedDecisionKey(null);
    if (bladeId === BLADE_REVIEW) {
      void loadReview(reviewScope);
    }
    if (bladeId === BLADE_AGENTS) {
      void loadBoard(boardScope);
    }
    if (bladeId === BLADE_OVERVIEW) {
      void loadOverview();
    }
    if (bladeId === BLADE_DECISIONS) {
      void loadDecisions(decisionsScope);
    }
    if (bladeId === BLADE_INSTANCES) {
      void loadInstances();
    }
    if (bladeId === BLADE_SYSTEM_ACTIONS) {
      void loadSystemActions();
    }
    if (bladeId === BLADE_GIT_CHECKOUTS) {
      void loadGitCheckouts();
    }
  };

  const changeBoardScope = (scope: ConsoleAgentsScope) => {
    setBoardScope(scope);
    void loadBoard(scope);
  };

  const changeDecisionsScope = (scope: ConsoleAgentsScope) => {
    setDecisionsScope(scope);
    void loadDecisions(scope);
  };

  const changeReviewScope = (scope: ConsoleAgentsScope) => {
    setReviewScope(scope);
    void loadReview(scope);
  };

  /** Follow a Review card's link to its decision: open Decisions, at that decision. */
  const openDecision = (decisionKey: string) => {
    selectBlade(BLADE_DECISIONS);
    setFollowedDecisionKey(decisionKey);
  };

  /**
   * Send one ruling and show the list again as the server now holds it. The answer is
   * returned for the blade to show in the server's own words, whatever its outcome.
   */
  const ruleDecision = async (decisionKey: string, choice: ConsoleRulingChoice) => {
    const result = await ruleConsoleDecision(getIdToken, decisionKey, choice);
    void loadDecisions(decisionsScope);
    return result.body;
  };

  const openTrailById = async (conversationId: string) => {
    if (!conversationId) {
      return;
    }
    setActiveBladeId(BLADE_WORK);
    setSelectedId(conversationId);
    const result = await fetchConsoleTrail(getIdToken, conversationId);
    const conversation = historyConversation(result.body);
    if (conversation && Array.isArray(conversation.turns)) {
      setOpenTrail(conversation);
      setAnswer(null);
    }
  };

  const submitAsk = async (question: string) => {
    setAsking(true);
    setAnswer(null);
    try {
      const result = await askConsoleKnowledgeStream(
        getIdToken,
        question,
        selectedId || undefined,
        {
          onDelta: (text) => {
            setAnswer((current) => {
              const prior =
                current && "answer" in current ? String(current.answer || "") : "";
              return {
                response_type: "console_knowledge_answer",
                outcome_type: "success",
                routing: "CC1-CHAIN",
                reason: "ask_streaming",
                message: "",
                ceiling_applied: null,
                delivery_surface: "console_work",
                sources: [],
                citations: [],
                answer: prior + text,
                conversation_id: selectedId || undefined,
              };
            });
          },
        },
      );
      setAnswer(result.body);
      if (isAskAnswer(result.body)) {
        const conversationId = askConversationId(result.body) || selectedId;
        if (conversationId) {
          setSelectedId(conversationId);
          try {
            const trail = await fetchConsoleTrail(getIdToken, conversationId);
            const conversation = historyConversation(trail.body);
            if (conversation && Array.isArray(conversation.turns) && conversation.turns.length) {
              setOpenTrail(conversation);
              setAnswer(null);
            }
          } catch {
            // Keep the live answer on screen when history read cannot reopen it.
          }
        }
      }
      await loadHistory(0);
    } finally {
      setAsking(false);
    }
  };

  const identityName =
    session?.identity.display_name ||
    session?.identity.upn ||
    account?.name ||
    account?.username ||
    "Signed in";
  const identityEmail = session?.identity.upn || account?.username || "";
  const holdsARole = (session?.roles_projected.length ?? 0) > 0;

  if (!ready) {
    return (
      <AppBootScreen
        title={brandingError || "Loading Cortex…"}
        data-testid="app-boot-screen"
      />
    );
  }

  return (
    <Layout
      identityName={identityName}
      identityEmail={identityEmail}
      onSignOut={() => void signOut()}
      isSidebarCollapsed={sidebarCollapsed}
      onSidebarCollapseChange={setSidebarCollapsed}
      chatHistoryPanel={
        holdsARole ? (
          <ChatHistoryPanel
            blades={session?.allowed_blades ?? []}
            activeBladeId={activeBladeId}
            onSelectBlade={selectBlade}
            conversations={conversations}
            selectedId={selectedId}
            loading={historyLoading}
            error={historyError}
            hasMore={hasMore}
            searchQuery={searchQuery}
            searchOpen={searchOpen}
            onSearchQueryChange={setSearchQuery}
            onToggleSearch={() => setSearchOpen((value) => !value)}
            onNew={startNew}
            onRefresh={() => void loadHistory(0)}
            onCollapse={() => setSidebarCollapsed(true)}
            onSelect={(id) => void openTrailById(id)}
            onRename={(id, title) => {
              void renameConsoleTrail(getIdToken, id, title).then(() => loadHistory(0));
            }}
            onDelete={(id) => {
              void deleteConsoleTrail(getIdToken, id).then(() => {
                if (selectedId === id) {
                  startNew();
                }
                void loadHistory(0);
              });
            }}
            onLoadMore={() => void loadHistory(conversations.length)}
            ingestTokenSource={getIdToken}
          />
        ) : undefined
      }
    >
      {loading ? (
        <ContentRegionLoader
          label="Loading your Cortex Console session"
          data-testid="console-session-loading"
        />
      ) : null}
      {!loading && refusal ? (
        <p data-testid="console-session-refusal">{refusal.message}</p>
      ) : null}
      {!loading && session && !holdsARole ? (
        <p data-testid="console-no-role">{NO_ROLE_MESSAGE}</p>
      ) : null}
      {!loading && session && holdsARole && activeBladeId === BLADE_AGENTS ? (
        <AgentsBlade
          board={board}
          loading={boardLoading}
          requestedScope={boardScope}
          onScopeChange={changeBoardScope}
          onRefresh={() => void loadBoard(boardScope)}
        />
      ) : null}
      {!loading && session && holdsARole && activeBladeId === BLADE_OVERVIEW ? (
        <OverviewBlade
          overview={overview}
          loading={overviewLoading}
          onRefresh={() => void loadOverview()}
        />
      ) : null}
      {!loading && session && holdsARole && activeBladeId === BLADE_DECISIONS ? (
        <DecisionsBlade
          decisions={decisions}
          loading={decisionsLoading}
          requestedScope={decisionsScope}
          onScopeChange={changeDecisionsScope}
          onRefresh={() => void loadDecisions(decisionsScope)}
          onRule={ruleDecision}
          followedDecisionKey={followedDecisionKey}
        />
      ) : null}
      {!loading && session && holdsARole && activeBladeId === BLADE_REVIEW ? (
        <ReviewBlade
          review={review}
          loading={reviewLoading}
          requestedScope={reviewScope}
          onScopeChange={changeReviewScope}
          onRefresh={() => void loadReview(reviewScope)}
          onOpenDecision={openDecision}
        />
      ) : null}
      {!loading && session && holdsARole && activeBladeId === BLADE_INSTANCES ? (
        <InstancesBlade
          instances={instances}
          loading={instancesLoading}
          onRefresh={() => void loadInstances()}
        />
      ) : null}
      {!loading && session && holdsARole && activeBladeId === BLADE_SYSTEM_ACTIONS ? (
        <SystemActionsBlade
          systemActions={systemActions}
          loading={systemActionsLoading}
          onRefresh={() => void loadSystemActions()}
        />
      ) : null}
      {!loading && session && holdsARole && activeBladeId === BLADE_GIT_CHECKOUTS ? (
        <GitCheckoutsBlade
          gitCheckouts={gitCheckouts}
          loading={gitCheckoutsLoading}
          onRefresh={() => void loadGitCheckouts()}
        />
      ) : null}
      {!loading &&
      session &&
      holdsARole &&
      activeBladeId !== BLADE_AGENTS &&
      activeBladeId !== BLADE_OVERVIEW &&
      activeBladeId !== BLADE_DECISIONS &&
      activeBladeId !== BLADE_REVIEW &&
      activeBladeId !== BLADE_INSTANCES &&
      activeBladeId !== BLADE_SYSTEM_ACTIONS &&
      activeBladeId !== BLADE_GIT_CHECKOUTS ? (
        <KnowledgeThread
          clearanceNotice={session.clearance_notice ?? null}
          asking={asking}
          answer={answer}
          openTrail={openTrail}
          onSubmit={(question) => void submitAsk(question)}
        />
      ) : null}
    </Layout>
  );
}
